import { canChangeShippingModel } from 'core/middlewares/effects/helpers';
import { put, select } from 'core/middlewares/utilities';
import {
  app,
  cart,
  catalog,
  checkout,
  combo,
  content,
  order,
  search,
  user,
  utilities,
  shippingModel,
  productDetail,
  showcases,
} from 'core/use-cases';
import { Cookies, getLocationPathname } from 'core/utilities';
import { noticeError } from 'core/utilities/helpers/notice-error';
import { OldHistory } from 'ui/components/History';

import { CART_ERROR_ID, errorHandleInvalidCredentials, hasNotification } from './error-handling';
import { showNotificationMinimumOrderAmount } from './show-notification-minimum-order-amount';
import { messages, notification, persistedExpiration } from './tasks';

import type {
  StarlinkDocumentMetaFailure,
  DataError,
  ResellerDeliveryMode,
  BffCommonDataError,
  DataChangeCycleError,
  DataCyclesFetchError,
  DataDeliveriesFetchError,
} from 'core/domains';
import type { RootState } from 'core/store';

/**
 * Middleware responsible for handle erro notifications
 *
 */
export function* errorFlow(action: utilities.ActionFailure) {
  const { error } = action;
  if (
    errorHandleInvalidCredentials(action) &&
    action.type !== user.actions.changeCycle.failure.type &&
    action.type !== showcases.actions.showcasesFetch.failure.type
  ) {
    OldHistory.navigate('/401');
    yield put(app.actions.setLoading(''));

    return;
  }

  const pathname = getLocationPathname();

  const isErrorCart =
    action.type === cart.actions.itemDeleteFetch.failure.type ||
    action.type === cart.actions.itemUpdateFetch.failure.type ||
    action.type === cart.actions.itemAddFetch.failure.type ||
    action.type === cart.actions.cartSetShippingModelFetch.failure.type ||
    action.type === combo.actions.comboPost.failure.type ||
    action.type === cart.actions.cartFetchForGiftsCheck.failure.type;

  const isErrorCompletedOrder = action.type === checkout.actions.paymentDetailFetch.failure.type;

  if (isErrorCompletedOrder) {
    yield put(order.actions.orderFetch.request());

    return;
  }

  if (action.type === catalog.actions.searchPreviousFetch.failure.type) {
    return;
  }

  if (action.type === checkout.actions.processFetch.failure.type) {
    yield put(app.actions.setLoading(''));

    const errorId = action?.error?.response?.errorId || '';

    if (CART_ERROR_ID.MINIMUM_ORDER_AMOUNT === errorId) {
      yield showNotificationMinimumOrderAmount();

      return;
    }

    OldHistory.navigate('/process-error');

    return;
  }

  const isShippingModel: boolean = yield canChangeShippingModel();
  const isErrorCartShippingModel = action.type === cart.actions.itemAddFetch.failure.type;

  if (
    isErrorCart ||
    action.type === cart.actions.cartFetch.failure.type ||
    action.type === cart.actions.lastCartFetch.failure.type ||
    action.type === cart.actions.cartCurrentDeliveryFetch.failure.type
  ) {
    yield put(app.actions.setLoading(''));
  }

  if (action.type === cart.actions.itemUpdateFetch.failure.type) {
    const { productCode, quantity } = action.meta?.payload as unknown as {
      productCode: string;
      quantity: number;
    };

    const cartItems: RootState['cart']['data']['items'] = yield select(
      (rootState: RootState) => rootState.cart.data.items,
    );

    const updatedItem = cartItems.find((item) => item.productCode === productCode);
    if (updatedItem && quantity < updatedItem.quantity) {
      yield put(app.actions.setLoading(''));

      yield notification.error(
        action,
        'errorRemoveProduct',
        messages.failure[cart.actions.itemDeleteFetch.failure.type],
      );

      return;
    }
  }

  if (
    action.type === cart.actions.setCouponFetch.failure.type ||
    action.type === cart.actions.clearCouponFetch.failure.type
  ) {
    yield put(app.actions.setLoading(''));

    return;
  }

  if (action.type === user.actions.cyclesFetch.failure.type) {
    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      (rootState: RootState) => rootState.user.data.resellerId,
    );
    const currentCycle: RootState['user']['data']['cycle']['currentCycle'] = yield select(
      (rootState: RootState) => rootState.user.data.cycle.currentCycle,
    );

    const currentPath: string = getLocationPathname();

    yield put(app.actions.setLoading(''));

    if (currentPath !== '/401') {
      yield notification.error(action);
    }
  }

  if (action.type === shippingModel.actions.availableDeliveryModesFetch.failure.type) {
    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      (rootState: RootState) => rootState.user.data.resellerId,
    );

    yield put(app.actions.setLoading(''));
    yield notification.error(action);

    return;
  }

  if (action.type === shippingModel.actions.currentDeliveryModeFetch.failure.type) {
    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      (rootState: RootState) => rootState.user.data.resellerId,
    );

    const {
      meta: { payload },
    } = action as unknown as { meta: { payload: ResellerDeliveryMode } };

    yield put(app.actions.setLoading(''));
    yield notification.error(action);

    return;
  }

  if (action.type === user.actions.changeCycle.failure.type) {
    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      (rootState: RootState) => rootState.user.data.resellerId,
    );
    const nextCycle: RootState['user']['data']['cycle']['nextCycle'] = yield select(
      (rootState: RootState) => rootState.user.data.cycle.nextCycle,
    );

    const data = {
      resellerId,
      detail: error?.response?.detail,
      nextCycle,
    };

    noticeError<DataChangeCycleError>('change-cycle-error', data);

    window.dataLayer.push({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'callback_atualizar_ciclo_erro',
        cd_error_message: error?.response?.detail,
      },
    });
  }

  if (action.type === checkout.actions.promotionFetch.failure.type) {
    yield put(app.actions.setLoading(''));

    yield notification.error(action);

    return;
  }

  if (
    action.type === content.actions.starlinkPageFetch.failure.type ||
    action.type === content.actions.starlinkContentFetch.failure.type
  ) {
    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      (rootState: RootState) => rootState.user.data.resellerId,
    );
    const {
      meta: { payload },
    } = action as unknown as { meta: StarlinkDocumentMetaFailure };

    return;
  }

  if (action.type === catalog.actions.getResellerAvailableCatalogs.failure.type) {
    return;
  }

  if (action.type === productDetail.actions.productDetailFetch.failure.type) {
    return;
  }

  if (action.type === user.actions.userResaleIntention.failure.type) {
    yield put(user.actions.userFetch.request());
    return;
  }

  yield persistedExpiration(action);

  switch (action.error?.status) {
    case 206: {
      if (action.type === checkout.actions.paymentMethodsFetch.failure.type) {
        return;
      }

      yield notification.error(action);

      return;
    }

    case 400: {
      const { response } = action.error;
      if (response?.errorId === CART_ERROR_ID.PRODUCT_QUOTA_EXCEEDED) {
        yield notification.error(action, 'errorQuotaAddProduct');

        return;
      }

      if (response?.errorId === CART_ERROR_ID.UNCOMMITTED_ITEM_API_EXCEPTION && !isShippingModel) {
        yield notification.error(action, 'errorAddProduct');

        return;
      }

      if (isShippingModel && isErrorCartShippingModel) {
        yield put(app.actions.toggleProductNotFoundShipping(true));

        return;
      }

      if (isErrorCart) {
        yield notification.error(action, 'errorAddProduct');
        yield put(cart.actions.cartFetch.request());

        return;
      }

      if (action.type === checkout.actions.paymentMethodsFetch.failure.type) {
        return;
      }

      if (action.type === combo.actions.comboFetch.failure.type) {
        OldHistory.navigate('/500');

        return;
      }

      if (action.type === checkout.actions.paymentDetailNewFlowFetch.failure.type) {
        return;
      }

      if (hasNotification(action, pathname)) {
        return;
      }

      yield notification.error(action);

      return;
    }

    case 401: {
      if (action.type === showcases.actions.showcasesFetch.failure.type) {
        return;
      }

      Cookies.remove('currentCycle');

      OldHistory.navigate('/401');

      return;
    }

    case 403: {
      const { response } = action.error;

      if (response?.errorId === CART_ERROR_ID.REQUEST_BLOCKED_BY_CADASTRAL_SITUATION) {
        OldHistory.navigate('/403');

        return;
      }

      if (hasNotification(action, pathname)) {
        return;
      }

      if (action.type !== user.actions.changeCycle.failure.type) {
        yield notification.error(action);
      }
      return;
    }

    case 500: {
      const actionsError = [
        cart.actions.cartFetch.failure.type,
        cart.actions.lastCartFetch.failure.type,
        catalog.actions.catalogFetch.failure.type,
        combo.actions.comboFetch.failure.type,
        search.actions.searchFetch.failure.type,
        cart.actions.cartCurrentDeliveryFetch.failure.type,
      ];

      const actionsErrorAddProducts = [
        cart.actions.itemAddFetch.failure.type,
        cart.actions.itemUpdateFetch.failure.type,
      ];

      const isErrorFech = actionsError.some((elemento) => action.type.includes(elemento));
      const isErrorPAddProducts = actionsErrorAddProducts.some((elemento) =>
        action.type.includes(elemento),
      );
      if (isErrorFech) {
        OldHistory.navigate('/500');

        return;
      }

      if (action.type === cart.actions.itemDeleteFetch.failure.type) {
        yield put(app.actions.setLoading(''));

        yield notification.error(action, 'errorRemoveProduct');

        return;
      }

      if (isErrorPAddProducts) {
        yield put(app.actions.setLoading(''));

        yield notification.error(
          action,
          'errorAddProduct',
          messages.failure[cart.actions.itemAddFetch.failure.type],
        );

        return;
      }

      if (action.type === checkout.actions.paymentMethodsFetch.failure.type) {
        return;
      }

      if (hasNotification(action, pathname)) {
        return;
      }

      yield notification.error(action);

      return;
    }

    case 502: {
      if (
        action.type === checkout.actions.createOrderFetch.failure.type ||
        action.type === checkout.actions.createOrderFetchNewFlow.failure.type
      ) {
        return;
      }

      yield notification.error(action);

      return;
    }

    case 404: {
      const { response } = action.error;
      yield put(app.actions.setLoading(''));

      if (response?.errorId === CART_ERROR_ID.BUSINESS_MODEL_NOT_FOUND) {
        OldHistory.navigate('/403');

        return;
      }

      if (hasNotification(action, pathname)) {
        return;
      }

      yield notification.error(action);

      return;
    }

    default: {
      if (hasNotification(action, pathname)) {
        return;
      }

      yield notification.error(action);
    }
  }
}
